import { Button, Form, Space, Table, Tooltip } from 'antd';
import { Fragment, useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getConsultationsListApi } from '@services/Uc/Consultations';
import dayjs from 'dayjs';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import PageHeader from '@components/PageHeader/PageHeader';
import ConsultationsFilterForm from './ConsultationsFilterForm';
import './ConsultationsList.less';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

export default function ConsultationsList() {
    const [form] = Form.useForm();

    // 获取列表数据
    const [data, setData] = useState([]);
    const [queryObj, setQueryObj] = useState({});
    // 分页设置
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 分页
    const onChangePage = (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const handleChangeTime = () => {};

    const onFinish = res => {
        setPage(1);
        setQueryObj(res);
    };
    const onReset = () => {
        form.resetFields();
        setPage(1);
        setQueryObj({});
    };
    useEffect(() => {
        const queryParams = {
            ...form.getFieldsValue(),
            page,
            limit: pageSize,
        };
        if (queryObj.time != null) queryParams.startTime = dayjs(queryObj.time[0]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.time != null) queryParams.endTime = dayjs(queryObj.time[1]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.followStatus === -1) {
            delete queryParams.followStatus;
        }
        delete queryParams.time;
        getConsultationsListApi(queryParams).then(res => {
            if (res.status_code === 200) {
                setData(res.data.list);
                setTotal(res.data.totalCount);
            }
        });
    }, [form, page, pageSize, queryObj]);

    const columns = [
        { title: '用户昵称', dataIndex: 'nickName', width: 200 },
        { title: '手机号', dataIndex: 'memMobile', width: 100 },
        { title: '咨询内容', dataIndex: 'message', width: 400 },
        { title: '咨询类型', dataIndex: 'type', width: 100, render: (_, row) => <Fragment>{row.type === 1 && <span> 解决方案 </span>}</Fragment> },
        { title: '内容信息', dataIndex: 'content', width: 400 },
        { title: '咨询时间', dataIndex: 'createdAt', width: 300 },
        {
            title: '最后跟进',
            dataIndex: 'time',
            width: 300,
            render: (_, row) => (
                <Fragment>
                    {row.followName && <p>{row.followName}</p>}
                    {row.followTime && <p>{row.followTime}</p>}
                </Fragment>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 200,
            render: (_, row) => (
                <Fragment>
                    <Link to={`/uc/user/consultations/${row.id}`}>查看详情</Link>
                </Fragment>
            ),
        },
    ];

    return (
        <div className="page-container list-page-container consultations-page">
            <PageHeader title="用户咨询管理" breadcrumb={[{ label: '用户中心', path: '/uc' }, { label: '用户咨询管理' }]} {...filterToggleProps} />
            <FilterPanelSlot>
                <ConsultationsFilterForm form={form} onFinish={onFinish} onReset={onReset} onChangeTime={handleChangeTime} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container consultations-table-region">
                <div className="main-toolbar consultations-table-toolbar">
                    <div className="main-tools">
                        <span className="consultations-table-title">咨询列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap consultations-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always enterprises-table' : 'enterprises-table'}
                        columns={columns}
                        dataSource={data}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        sticky
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
